/**
 * leviathans — 1–3 vast, slow, monotone creature silhouettes drifting across the
 * upper field (the whale-skeleton drift). `critters` scaled to cathedral
 * proportions + slowed + given depth planes + a procedural silhouette. The
 * signature of the sunken biome.
 *
 * Each whale: generate silhouette geometry ONCE → draw ONCE into an IGraphics
 * (fill + rib strokes + optional rim) → animate by TRANSFORM ONLY (x drift +
 * y bob + alpha). No per-frame graphics redraw. This is the "draw once, animate
 * by transform" perf rule (BUILD_STANDARDS gotcha).
 *
 * Degradation (BUILD_STANDARDS §3): createGraphics returns null if the factory
 * isn't registered (headless/cold-load) → build() returns null → AtmosphereLayer
 * skips the layer (quieter scene, never a crash). Budget exhausted → break.
 * Reduce-motion → freeze travel + bob (whales stay positioned, more ominous).
 *
 * VQA (BUILD_STANDARDS §2): spriteCount() returns the whale count (1–3) so
 * __atmosphere.spriteCount() includes them. REGION=0,0.1,1,0.4 isolates the
 * upper field. Interior opt-out → capture at phase 0.0 and 0.5, diff must be
 * identical (the day clock doesn't touch them).
 *
 * @see docs/design/systems/leviathans-effect-kind.md — blueprint.
 */
import { registerEffect, makeContentHandle, type EffectBuilder } from '../effectBuilders';
import { seededRand } from '../atmospherePlacement';
import { createGraphics } from '../../../rendering/utils/Graphics';
import { bakeGraphicsToSprite } from '../util/bakeGraphicsToSprite';
import { generateSilhouette } from '../silhouettes';
import { edgeHighlightAlpha, type EdgeHighlightSpec } from '../util/edgeHighlight';
import { vfxBudgetManager } from '../../VFXBudgetManager';
import { getMotionPrefs } from '../../../systems/accessibility/motionPrefs';
import { Z_LAYERS } from '../../../rendering/Z_LAYERS';
import { lerp } from '../../../../shared/utils/MathUtils';
import type { IGraphics, ISprite } from '../../../rendering/IRenderer';
import type { LeviathanSpec } from '../../../../config/atmosphere/effectLayer.types';

/** Default rim color — pale cold bone-white (LIT for additive). */
const DEFAULT_EDGE_COLOR = 0xbfeae0;
/** Default rim stroke width (px). */
const EDGE_WIDTH_PX = 2;
/** Rim LFO period (ms) — slow, non-strobing. */
const EDGE_LFO_PERIOD_MS = 6000;
/** Rim LFO alpha range — gentle breathing. */
const EDGE_ALPHA_MIN = 0.15;
const EDGE_ALPHA_MAX = 0.35;
/** Default vertical band (upper field). */
const DEFAULT_BAND_Y_MIN = 0.1;
const DEFAULT_BAND_Y_MAX = 0.45;

/** Fallback adapter: wraps a live IGraphics as a minimal ISprite so the
 *  Leviathan struct can use the same API whether baked or live. Only the
 *  transform + alpha + destroy methods are wired (that's all leviathans use). */
function makeGraphicsAsSprite(gfx: IGraphics): ISprite {
  return {
    x: 0, y: 0, scaleX: 1, scaleY: 1,
    setPosition: (x: number, y: number) => gfx.setPosition(x, y),
    setDisplaySize: () => {}, setFlipX: () => {}, setAngle: () => {},
    setScale: () => {}, setOrigin: () => {},
    setVisible: (v: boolean) => gfx.setVisible(v),
    setAlpha: (a: number) => gfx.setAlpha(a),
    setTint: () => {}, clearTint: () => {},
    setDepth: () => {}, // depth set at construction via GraphicsStyle
    setBlendMode: (m: 'normal' | 'add' | 'screen') => gfx.setBlendMode(m),
    setInteractive: () => {}, onPointerDown: () => {}, onPointerOver: () => {}, onPointerOut: () => {},
    getBounds: () => ({ x: 0, y: 0, width: 0, height: 0 }),
    hasTexture: () => false, setTexture: () => false,
    loadTexture: () => Promise.resolve(),
    tween: () => {}, killAllTweens: () => {},
    destroy: () => gfx.destroy(),
  } as unknown as ISprite;
}

/** One drifting whale instance. */
interface Leviathan {
  /** Baked sprite (fill + ribs) — lightweight transform-only animation. */
  sprite: ISprite;
  /** Edge-highlight rim (separate sprite so its alpha can LFO independently). */
  edgeSprite: ISprite | null;
  x: number;
  y: number;
  vx: number;
  bobAmp: number;
  bobPeriod: number;
  bobPhase: number;
  baseAlpha: number;
  edgeSpec: EdgeHighlightSpec | null;
}

const buildLeviathans: EffectBuilder = (layer, ctx) => {
  if (layer.kind !== 'leviathans') return null;
  const spec = layer as LeviathanSpec;
  const { region } = ctx;
  const reduceMotion = getMotionPrefs().reduceMotion;
  const bandYMin = spec.bandYMin ?? DEFAULT_BAND_Y_MIN;
  const bandYMax = spec.bandYMax ?? DEFAULT_BAND_Y_MAX;
  const bandY0 = region.y0 + bandYMin * (region.y1 - region.y0);
  const bandY1 = region.y0 + bandYMax * (region.y1 - region.y0);

  const leviathans: Leviathan[] = [];
  let instanceIdx = 0;

  for (let pi = 0; pi < spec.planes.length; pi += 1) {
    const plane = spec.planes[pi];
    for (let i = 0; i < plane.count; i += 1) {
      if (!vfxBudgetManager.canSpawnShape()) break;
      const seed = ctx.seed + instanceIdx * 73.31 + pi * 911;
      const width = lerp(plane.widthMin, plane.widthMax, seededRand(seed));
      const speed = lerp(plane.speedMin, plane.speedMax, seededRand(seed + 1));
      const dirSign = spec.direction === 'left' ? -1
        : spec.direction === 'right' ? 1
        : (instanceIdx % 2 === 0 ? 1 : -1);
      // Start spread across the region so they don't all enter at once.
      const x = lerp(region.x0, region.x1, seededRand(seed + 2));
      const y = lerp(bandY0, bandY1, seededRand(seed + 3));

      // Generate the silhouette geometry ONCE (deterministic per seed).
      const geo = generateSilhouette({
        shape: spec.shape,
        seed: Math.floor(seed),
        width,
        detail: spec.detail ?? 1,
      });

      // BAKE: draw the hull + ribs ONCE into a RenderTexture → lightweight sprite.
      // The sprite transforms cheaply (x/y/alpha = uniforms); geometry is never
      // re-tessellated. Falls back to IGraphics if bake factory isn't registered.
      const textureKey = `leviathan_${spec.shape}_${Math.floor(seed)}`;
      const sprite = bakeGraphicsToSprite({
        draw: (gfx: IGraphics) => {
          gfx.fillPolygon(geo.outline, plane.tint, plane.alpha);
          for (const part of geo.parts) {
            gfx.strokePolyline(part.points, plane.tint, 1.5, plane.alpha * 0.8);
          }
        },
        graphicsStyle: { depth: plane.depth, parent: ctx.layerContainer },
        spriteOpts: { x, y, width, height: width * 0.3, depth: plane.depth, alpha: plane.alpha, parent: ctx.layerContainer },
        textureKey,
      });

      // Degradation path: bake factory not registered (headless) → fall back
      // to live IGraphics (the old path). Still works, just less efficient.
      let activeSprite: ISprite | null = sprite;
      if (!sprite) {
        const fallbackGfx = createGraphics(null, { depth: plane.depth, parent: ctx.layerContainer });
        if (!fallbackGfx) break; // factory not registered either → skip
        fallbackGfx.fillPolygon(geo.outline, plane.tint, plane.alpha);
        for (const part of geo.parts) {
          fallbackGfx.strokePolyline(part.points, plane.tint, 1.5, plane.alpha * 0.8);
        }
        fallbackGfx.setPosition(x, y);
        // Wrap the fallback IGraphics as a minimal ISprite-like object.
        activeSprite = makeGraphicsAsSprite(fallbackGfx);
      }

      // Optional edge-highlight rim (baked separately so its alpha can LFO independently).
      let edgeSprite: ISprite | null = null;
      let edgeSpec: EdgeHighlightSpec | null = null;
      if (spec.edgeHighlight) {
        edgeSpec = {
          color: spec.edgeColor ?? DEFAULT_EDGE_COLOR,
          widthPx: EDGE_WIDTH_PX,
          alphaMin: EDGE_ALPHA_MIN,
          alphaMax: EDGE_ALPHA_MAX,
          periodMs: EDGE_LFO_PERIOD_MS,
        };
        const edgeKey = `${textureKey}_edge`;
        edgeSprite = bakeGraphicsToSprite({
          draw: (gfx: IGraphics) => {
            gfx.strokePolyline(geo.outline, edgeSpec!.color, edgeSpec!.widthPx, edgeSpec!.alphaMax);
          },
          graphicsStyle: { depth: plane.depth, parent: ctx.layerContainer },
          spriteOpts: { x, y, width, height: width * 0.3, depth: plane.depth, alpha: edgeSpec.alphaMax, parent: ctx.layerContainer },
          textureKey: edgeKey,
        });
        if (!edgeSprite) {
          // Fallback: live IGraphics for the rim.
          const edgeGfx = createGraphics(null, { depth: plane.depth, parent: ctx.layerContainer });
          if (edgeGfx) {
            edgeGfx.strokePolyline(geo.outline, edgeSpec.color, edgeSpec.widthPx, edgeSpec.alphaMax);
            edgeGfx.setPosition(x, y);
            edgeSprite = makeGraphicsAsSprite(edgeGfx);
          }
        }
      }

      // Position the sprite at the spawn point (anchor: center).
      // activeSprite is non-null here — if both bake and fallback fail, we broke above.
      activeSprite!.setPosition(x, y);
      edgeSprite?.setPosition(x, y);

      leviathans.push({
        sprite: activeSprite!,
        edgeSprite,
        x,
        y,
        vx: speed * dirSign,
        bobAmp: spec.bob.ampPx,
        bobPeriod: lerp(spec.bob.periodMinMs, spec.bob.periodMaxMs, seededRand(seed + 4)),
        bobPhase: seededRand(seed + 5),
        baseAlpha: plane.alpha,
        edgeSpec,
      });
      instanceIdx += 1;
    }
  }

  if (!leviathans.length) return null;

  // Reduced motion: leave whales positioned statically, no tick.
  if (reduceMotion) {
    return makeContentHandle({
      sprites: [],
      spriteCount: () => leviathans.length,
      onDestroy: () => {
        for (const l of leviathans) {
          try { l.sprite.destroy(); } catch { /* gone */ }
          try { l.edgeSprite?.destroy(); } catch { /* gone */ }
        }
      },
    });
  }

  // Animate by TRANSFORM ONLY: x drift + y bob + edge alpha LFO.
  let elapsed = 0;
  const tick = (dtMs: number) => {
    elapsed += dtMs;
    const spanX0 = region.x0 - 200;
    const spanX1 = region.x1 + 200;
    for (const l of leviathans) {
      l.x += (l.vx * dtMs) / 1000;
      // Wrap at the far edge (recycle to the opposite edge).
      if (l.vx > 0 && l.x > spanX1) l.x = spanX0;
      else if (l.vx < 0 && l.x < spanX0) l.x = spanX1;
      // Vertical bob (seeded phase per instance — no sync).
      const bobY = l.y + Math.sin((elapsed / l.bobPeriod) * Math.PI * 2 + l.bobPhase * Math.PI * 2) * l.bobAmp;
      l.sprite.setPosition(l.x, bobY);
      l.edgeSprite?.setPosition(l.x, bobY);
      // Edge rim LFO.
      if (l.edgeSprite && l.edgeSpec) {
        const a = edgeHighlightAlpha(l.edgeSpec, elapsed, l.bobPhase);
        l.edgeSprite.setAlpha(a);
      }
    }
  };
  const unsubTick = ctx.addTick(tick);

  return makeContentHandle({
    sprites: [],
    spriteCount: () => leviathans.length,
    onDestroy: () => {
      try { unsubTick(); } catch { /* gone */ }
      for (const l of leviathans) {
        try { l.sprite.destroy(); } catch { /* gone */ }
        try { l.edgeSprite?.destroy(); } catch { /* gone */ }
      }
    },
  });
};

registerEffect({
  kind: 'leviathans',
  build: buildLeviathans,
  depth: Z_LAYERS.LEVIATHAN_FAR,
  mooded: true,
});
